import * as THREE from 'three';
import { lerp, lerpAngle } from '../core/math';
import type { Actor } from '../sim/actor';
import { type ActorUniforms, type OutlineUniforms, actorUniforms, addOutline, outlineUniforms, patchActorMaterial } from './actorShading';
import type { AssetLibrary, ModelTemplate } from './assets';
import { BREAKABLE_MESHES, CLIP_TOOL, GEARLESS_MODELS, type ToolId, WEAPON_GEAR, bossFitScale } from './modelMap';

/** Fallbacks when a model lacks a clip a skill asks for. */
const CLIP_FALLBACK: Record<string, string[]> = {
  Spin: ['Attack2', 'Attack1'],
  Slam: ['Attack3', 'Attack2', 'Attack'],
  Leap: ['Slam', 'Attack3', 'Attack'],
  Channel: ['Cast', 'Attack1'],
  Shoot: ['Attack1', 'Cast', 'Attack'],
  Kick: ['Attack2', 'Attack1'],
  Buff: ['Cast', 'Roar', 'Idle'],
  Charge: ['Cast', 'Idle'],
  Uppercut: ['Attack2', 'Attack1'],
  Roar: ['Buff', 'Cast', 'Attack'],
  Breath: ['Cast', 'Attack2', 'Attack'],
  Attack: ['Attack1'],
  Attack1: ['Attack'],
  Attack2: ['Attack', 'Attack1'],
  Attack3: ['Attack2', 'Attack'],
  Cast: ['Breath', 'Attack2', 'Attack'],
  Dash: ['Run'],
  Knockdown: ['Hit', 'Stun'],
  GetUp: ['Idle'],
  Stagger: ['Stun', 'Hit'],
  Stun: ['Stagger', 'Hit'],
  Walk: ['Run'],
  Talk: ['Idle'],
  Wave: ['Emote_Wave', 'Idle'],
  Emote_Wave: ['Wave', 'Idle'],
  Victory: ['Emote_Cheer', 'Roar', 'Idle'],
  Gather: ['Attack1', 'Attack'],
  Fish: ['Idle'],
};

const LOOPING = new Set(['Idle', 'Run', 'Walk', 'Spin', 'Channel', 'Charge', 'Stun', 'Stagger', 'Gather', 'Fish', 'Emote_Dance', 'Emote_Sit', 'Talk']);

/**
 * Gameplay glows. Monster states: `counter` (blue, counter window open),
 * `staggered` (purple), `frozen`, `enraged`. Player identity modes: `burst`
 * (Ravager frenzy), `fiend` (Fiendbinder form), `overflow` / `torrent`
 * (Stormweaver), `parry` (Windfist Still Water, gold "just guard"), `surge`
 * (Windfist Surge of Chi), `identity` (any other active identity state, drawn
 * in the class hue set in `glowColor`). `invuln` and `hover` are free for
 * callers.
 */
export type Glow = 'none' | 'counter' | 'frozen' | 'staggered' | 'enraged' | 'invuln' | 'fiend' | 'burst' | 'hover' | 'overflow' | 'torrent' | 'parry' | 'surge' | 'identity';

interface GlowStyle {
  color: THREE.Color;
  /** Rim (fresnel) strength, its exponent (lower = wider) and flat body strength, HDR. */
  rim: number;
  pow: number;
  body: number;
  /** Pulses per second and depth (0..1). */
  hz: number;
  depth: number;
  /** Outline colour (bosses) while this glow is active; null keeps the red battle outline. */
  outline: THREE.Color | null;
  outlinePx: number;
  /** Aura particles per second (identity modes). */
  aura: number;
}

const style = (r: number, g: number, b: number, rim: number, pow: number, body: number, hz: number, depth: number, aura = 0, outline: THREE.Color | null = null, outlinePx = 3): GlowStyle => ({
  color: new THREE.Color(r, g, b),
  rim,
  pow,
  body,
  hz,
  depth,
  outline,
  outlinePx,
  aura,
});

/**
 * Monster states are loud gameplay signals (wide rim, body tint, outline);
 * player identity modes are a thinner rim plus an aura of rising motes so the
 * character stays readable.
 */
const GLOWS: Record<Exclude<Glow, 'none'>, GlowStyle> = {
  counter: style(0.25, 0.55, 1.0, 2.3, 1.6, 0.22, 3.2, 0.5, 0, new THREE.Color(0.4, 0.8, 2.2), 4.5),
  staggered: style(0.66, 0.36, 1.0, 2.0, 1.8, 0.16, 1.1, 0.35, 0, new THREE.Color(0.9, 0.5, 1.8), 3.5),
  frozen: style(0.45, 0.8, 1.0, 1.5, 2.0, 0.32, 0.4, 0.1, 0, new THREE.Color(0.6, 1.1, 1.6), 3),
  enraged: style(1.0, 0.1, 0.05, 1.7, 1.8, 0.1, 1.6, 0.35, 0, new THREE.Color(2.0, 0.12, 0.05), 3.5),
  invuln: style(1.0, 0.9, 0.6, 1.4, 2.2, 0.1, 2.0, 0.3),
  fiend: style(1.0, 0.12, 0.3, 1.3, 2.4, 0.03, 1.4, 0.35, 26),
  burst: style(1.0, 0.4, 0.1, 1.5, 2.4, 0.04, 2.2, 0.35, 26),
  hover: style(1.0, 0.25, 0.15, 1.0, 2.2, 0.0, 0, 0),
  overflow: style(0.55, 0.45, 1.0, 1.3, 2.4, 0.03, 1.2, 0.3, 18),
  torrent: style(0.55, 0.8, 1.0, 1.7, 2.2, 0.05, 5.5, 0.45, 30),
  parry: style(1.0, 0.8, 0.3, 1.9, 2.0, 0.08, 4.0, 0.3, 14),
  surge: style(0.45, 0.9, 1.0, 1.3, 2.4, 0.04, 2.4, 0.35, 18),
  identity: style(1.0, 1.0, 1.0, 1.0, 2.6, 0.02, 1.0, 0.3, 8),
};

const BATTLE_OUTLINE = new THREE.Color(1.0, 0.13, 0.05);

/** Clips that swing a melee weapon (weapon trails). */
const SWING_CLIPS = new Set(['Attack', 'Attack1', 'Attack2', 'Attack3', 'Spin', 'Slam', 'Leap', 'Kick']);
/** Melee weapon kinds with trails: sockets and blade length along socket +Y (m). */
const TRAIL_SPEC: Record<string, { sockets: string[]; length: number }> = {
  greatsword: { sockets: ['weapon'], length: 1.45 },
  dagger: { sockets: ['weapon', 'offhand'], length: 0.42 },
  gauntlet: { sockets: ['weapon', 'offhand'], length: 0.24 },
};
/** Gearless forms swing their claws. */
const CLAW_SOCKETS = ['hand_R', 'hand_L'];
const HIT_FLASH = 0.12;

/** One hand socket: the embedded default gear, and gear attached at runtime. */
interface Hand {
  bone: THREE.Object3D | null;
  defaults: THREE.Object3D[];
  /** Gear kind of the embedded default (`<id>_weapon_<kind>` → kind). */
  defaultKind: string | null;
  attached: THREE.Object3D | null;
  attachedId: string | null;
  /** Gear id wanted on this hand: a gear model id, 'default' or null (empty). */
  want: string | null;
}

export interface ActorViewOptions {
  /** Draw the boss battle outline (inverted hull). */
  outline?: boolean;
}

/**
 * Visual counterpart of a sim Actor: model instance, animation mixer and
 * state-driven clip selection, per-instance glow/flash, gear on the hand
 * sockets and breakable parts.
 *
 * Gear API (presentation only, never touches the sim):
 * - `setWeapon(kind)`: a weapon kind from `WEAPON_GEAR` (greatsword, staff,
 *   bellstaff, pistol, shotgun, rifle, gauntlet, dagger) on the `weapon` /
 *   `offhand` sockets; the model's embedded default gear is hidden while other
 *   gear is attached. `null` restores the default gear.
 * - `setTool(id)`: life-skill tool (`pickaxe`, `axe`, `sickle`, `shovel`,
 *   `fishing_rod`) in the weapon hand, the off hand is emptied; `null` puts
 *   the weapons back. While a Gather or Fish clip plays without an explicit
 *   tool, `toolHint` (or the clip default: sickle / fishing rod) is shown.
 * - `setPartBroken(partId)`: hides the mesh of a broken boss part.
 * - `setMount(model, seat)`: rides a vehicle (the ship at sea).
 *
 * Life-skill code: `zone.views.get(playerId)?.setTool('pickaxe')` before the
 * Gather/Fish loop and `setTool(null)` after it (tool ids: `modelMap.TOOLS`,
 * `modelMap.toolFor(kind, node)` maps an interactable to its tool).
 */
export class ActorView {
  readonly root = new THREE.Group();
  model: THREE.Object3D | null = null;
  private mixer: THREE.AnimationMixer | null = null;
  private clips = new Map<string, THREE.AnimationClip>();
  private actions = new Map<string, THREE.AnimationAction>();
  private current: THREE.AnimationAction | null = null;
  private currentName = '';
  /** One-shot clip currently playing on top of locomotion (skill stage, hit...). */
  private oneShotUntil = 0;
  private oneShotName = '';
  private time = 0;
  private materials: THREE.Material[] = [];
  private uniforms: ActorUniforms = actorUniforms();
  private outlineU: OutlineUniforms | null = null;
  private outline: { meshes: THREE.Mesh[]; material: THREE.Material } | null = null;
  private flashT = 0;
  glow: Glow = 'none';
  /** Colour for the `identity` glow (class hue). */
  glowColor = new THREE.Color(1, 1, 1);
  private glowLevel = 0;
  private glowShown = new THREE.Color();
  private glowStyle: GlowStyle = GLOWS.hover;
  private glowPhase = Math.random() * 10;
  /** Remaining counter window (s) to speed up the pulse as it closes. */
  counterLeft = 0;
  private yaw = 0;
  private visualPos = new THREE.Vector3();
  headHeight = 2;
  /** Blob shadow radius (m) and opacity, drawn in one batch by the zone. */
  blobRadius = 0.6;
  blobAlpha = 0.9;
  /** Last cast id whose stage animation was started. */
  private lastStageKey = '';
  private deathPlayed = false;
  scale = 1;
  readonly id: number;
  /** Requested model id (`actor.look.model`) this view was built from; the zone rebuilds the view when it changes. */
  readonly modelId: string;
  /** Resolved asset id. */
  readonly assetId: string;
  weaponBone: THREE.Object3D | null = null;
  offhandBone: THREE.Object3D | null = null;
  private hands: { main: Hand; off: Hand };
  private weaponKind: string | null = null;
  private tool: ToolId | null = null;
  /** Tool to show for Gather/Fish clips when none was set explicitly (e.g. from the nearest node). */
  toolHint: ToolId | null = null;
  private brokenParts = new Set<string>();
  private readonly lib: AssetLibrary;
  private readonly procedural: boolean;
  private spinners: THREE.Object3D[] = [];
  private bobbers: THREE.Object3D[] = [];
  /** Animation time not yet applied to the mixer (off-screen / far throttling). */
  private pendingAnim = 0;
  private animClock = 0;
  private disposed = false;
  private tint: number | undefined;
  private deathTime = 0;
  /** Tool currently applied to the hands (explicit or clip-driven). */
  private appliedTool: ToolId | null = null;
  /** Outline hulls of broken parts stay hidden. */
  private hiddenHulls = new Set<THREE.Mesh>();
  private trailBones: THREE.Object3D[] | null = null;
  /** Vehicle ridden (ship at sea) and the group that rolls with it. */
  private mountGroup: THREE.Group | null = null;
  private vehicle: THREE.Object3D | null = null;

  constructor(actor: Actor, template: ModelTemplate, lib: AssetLibrary, modelId: string = actor.look.model, opts: ActorViewOptions = {}) {
    this.id = actor.id;
    this.modelId = modelId;
    this.assetId = template.id;
    this.lib = lib;
    this.procedural = template.procedural;
    this.root.name = `actor_${actor.id}`;
    const fit = bossFitScale(actor.monster?.grade, actor.radius, template.entry?.radius, actor.look.scale ?? 1);
    this.scale = (actor.look.scale ?? 1) * fit;
    const model = lib.instantiate(template);
    model.scale.setScalar(this.scale);
    this.model = model;
    this.root.add(model);
    const h = template.entry?.height ?? 1.8;
    this.headHeight = h * this.scale + 0.35;
    const modelR = template.entry?.radius ?? actor.radius;
    this.blobRadius = Math.max(0.35, Math.min(modelR, Math.max(actor.radius, modelR * 0.6))) * this.scale * 1.25;

    // Monsters carry a content tint; players carry the class hue in `look.tint`, which is not a model tint.
    this.tint = actor.kind === 'monster' ? actor.look.tint : undefined;
    const cull = new THREE.Sphere(new THREE.Vector3(0, h * 0.5, 0), Math.max(h, modelR * 2) * 0.8 + 0.5);
    const main: Hand = { bone: null, defaults: [], defaultKind: null, attached: null, attachedId: null, want: 'default' };
    const off: Hand = { bone: null, defaults: [], defaultKind: null, attached: null, attachedId: null, want: 'default' };
    model.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) {
        m.material = this.adoptMaterials(m.material, template.placeholder);
        if ((m as THREE.SkinnedMesh).isSkinnedMesh) {
          // Bounds from the bind pose miss attacks and deaths: use a generous sphere instead.
          (m as THREE.SkinnedMesh).boundingSphere = cull.clone();
        }
        m.frustumCulled = true;
      }
      if (o.name === 'weapon') {
        this.weaponBone = o;
        main.bone = o;
      }
      if (o.name === 'offhand') {
        this.offhandBone = o;
        off.bone = o;
      }
      if (o.name === 'fx_spin') this.spinners.push(o);
      if (o.name === 'fx_bob') this.bobbers.push(o);
    });
    // Embedded default gear: `<id>_weapon_<kind>` / `<id>_offhand_<kind>` under the socket bones.
    for (const [hand, key] of [
      [main, '_weapon_'],
      [off, '_offhand_'],
    ] as const) {
      hand.bone?.traverse((o) => {
        const i = o.name.indexOf(key);
        if (i < 0 || o === hand.bone) return;
        hand.defaults.push(o);
        hand.defaultKind ??= o.name.slice(i + key.length);
      });
    }
    this.hands = { main, off };

    if (template.clips.length > 0) {
      this.mixer = new THREE.AnimationMixer(model);
      for (const c of template.clips) this.clips.set(c.name, c);
    }
    if (opts.outline && template.skinned) {
      this.outlineU = outlineUniforms();
      this.outline = addOutline(model, this.outlineU);
    }

    this.yaw = actor.facing;
    this.visualPos.set(actor.pos.x, 0, actor.pos.z);
    this.play('Idle', 0);
  }

  /** Clones a mesh's materials for this instance, tints them and adds the rim/glow terms. */
  private adoptMaterials(src: THREE.Material | THREE.Material[], placeholder: boolean): THREE.Material | THREE.Material[] {
    const list = Array.isArray(src) ? src : [src];
    const out = list.map((mat) => {
      const c = mat.clone();
      this.applyTint(c, placeholder);
      patchActorMaterial(c, this.uniforms);
      this.materials.push(c);
      return c;
    });
    return Array.isArray(src) ? out : (out[0] as THREE.Material);
  }

  private applyTint(m: THREE.Material, placeholder: boolean): void {
    const tint = this.tint;
    if (tint === undefined) return;
    const t = new THREE.Color(tint);
    const role = m.userData.tint as string | undefined;
    if (m instanceof THREE.MeshStandardMaterial && m.userData.merged) {
      // Merged body: vertex colours × a strong partial tint; glow accents take the variant hue.
      m.color.copy(new THREE.Color(1, 1, 1).lerp(t, 0.75));
      const max = Math.max(t.r, t.g, t.b, 1e-3);
      this.uniforms.uEmTint.value.setRGB(t.r / max, t.g / max, t.b / max);
      this.uniforms.uEmMode.value = 1;
    } else if (m instanceof THREE.MeshStandardMaterial) {
      if (placeholder || role === 'body') m.color.copy(t);
      else if (role === 'glow' || m.userData.glowMat) {
        // Glow accents take the variant hue at full brightness (red vs blue twins read at a glance).
        const max = Math.max(t.r, t.g, t.b, 1e-3);
        m.emissive.setRGB(t.r / max, t.g / max, t.b / max);
        m.color.copy(m.emissive);
      } else {
        // Vertex-coloured body: a strong partial multiply keeps the painted detail but reads as the variant.
        m.color.copy(new THREE.Color(1, 1, 1).lerp(t, 0.75));
      }
    } else if (m instanceof THREE.MeshBasicMaterial && role === 'light') {
      m.color.copy(t);
    }
  }

  // ------------------------------------------------------------------ gear

  /** Weapon kind for the hand sockets (`WEAPON_GEAR` key); null restores the model's own gear. */
  setWeapon(kind: string | null): void {
    if (kind === this.weaponKind) return;
    this.weaponKind = kind;
    this.trailBones = null;
    this.syncHands();
  }

  /** Life-skill tool in the weapon hand (null: weapons back in hand). */
  setTool(id: ToolId | null): void {
    if (id === this.tool) return;
    this.tool = id;
    this.syncHands();
  }

  get currentTool(): ToolId | null {
    return this.tool ?? this.autoTool();
  }

  private autoTool(): ToolId | null {
    const def = CLIP_TOOL[this.currentName];
    if (!def) return null;
    return this.toolHint ?? def;
  }

  private syncHands(): void {
    if (GEARLESS_MODELS.has(this.assetId)) return;
    const tool = this.tool ?? this.autoTool();
    this.appliedTool = tool;
    const { main, off } = this.hands;
    if (tool) {
      main.want = tool;
      off.want = null;
    } else if (this.weaponKind) {
      const gear = WEAPON_GEAR[this.weaponKind];
      main.want = gear ? gear.main : 'default';
      off.want = gear ? gear.off : 'default';
    } else {
      main.want = 'default';
      off.want = 'default';
    }
    this.applyHand(main);
    this.applyHand(off);
  }

  private applyHand(hand: Hand): void {
    if (!hand.bone) return;
    const want = hand.want;
    const useDefault = want === 'default' || (want !== null && want === hand.defaultKind);
    for (const d of hand.defaults) d.visible = useDefault;
    const gearId = useDefault ? null : want;
    if (hand.attachedId === gearId) return;
    if (hand.attached) {
      hand.bone.remove(hand.attached);
      this.releaseGear(hand.attached);
      hand.attached = null;
    }
    hand.attachedId = gearId;
    if (!gearId) return;
    void this.lib.load(gearId).then((t) => {
      if (this.disposed || hand.attachedId !== gearId || hand.attached || t.placeholder) return;
      const g = this.lib.instantiate(t);
      g.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh) return;
        m.material = this.adoptMaterials(m.material, false);
        m.castShadow = true;
        m.userData.gear = true;
      });
      g.name = `gear_${gearId}`;
      hand.attached = g;
      hand.bone?.add(g);
    });
  }

  private releaseGear(g: THREE.Object3D): void {
    g.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      for (const mat of Array.isArray(m.material) ? m.material : [m.material]) {
        this.materials = this.materials.filter((x) => x !== mat);
        mat.dispose();
      }
    });
  }

  // ------------------------------------------------------------------ trails

  /** True while a melee swing clip plays (and no tool is out). */
  get swinging(): boolean {
    return SWING_CLIPS.has(this.currentName) && this.oneShotUntil > this.time && !this.appliedTool;
  }

  /** Bones and blade length for weapon trails; null when the weapon in hand has none. */
  trailSockets(): { bones: THREE.Object3D[]; length: number } | null {
    const claws = GEARLESS_MODELS.has(this.assetId);
    const spec = claws ? { sockets: CLAW_SOCKETS, length: 0.5 } : this.weaponKind ? TRAIL_SPEC[this.weaponKind] : undefined;
    if (!spec || !this.model) return null;
    if (!this.trailBones) {
      const bones: THREE.Object3D[] = [];
      for (const name of spec.sockets) {
        const b = this.model.getObjectByName(name);
        if (b) bones.push(b);
      }
      this.trailBones = bones;
    }
    return this.trailBones.length ? { bones: this.trailBones, length: spec.length * this.scale } : null;
  }

  /** World-space hilt and tip of the blade on `bone` (socket +Y). */
  sampleBlade(bone: THREE.Object3D, length: number, hilt: THREE.Vector3, tip: THREE.Vector3): void {
    bone.updateWorldMatrix(true, false);
    const s = 1 / Math.max(1e-3, this.scale);
    hilt.set(0, length * 0.22 * s, 0).applyMatrix4(bone.matrixWorld);
    tip.set(0, length * s, 0).applyMatrix4(bone.matrixWorld);
  }

  // ------------------------------------------------------------------ mount

  /**
   * Rides a vehicle (the player's ship at sea): the body stands at `seat` on
   * it, locomotion shows Idle instead of Run and the whole rig rolls with the
   * swell. `null` dismounts.
   */
  setMount(vehicle: THREE.Object3D | null, seat: THREE.Vector3 = new THREE.Vector3()): void {
    const model = this.model;
    if (!model) return;
    if (this.vehicle) {
      this.mountGroup?.remove(this.vehicle);
      this.vehicle = null;
    }
    if (!vehicle) {
      if (this.mountGroup) {
        this.root.remove(this.mountGroup);
        this.mountGroup.remove(model);
        this.root.add(model);
        this.mountGroup = null;
      }
      model.position.set(0, 0, 0);
      return;
    }
    if (!this.mountGroup) {
      this.mountGroup = new THREE.Group();
      this.mountGroup.name = 'mount';
      this.root.remove(model);
      this.mountGroup.add(model);
      this.root.add(this.mountGroup);
    }
    this.vehicle = vehicle;
    this.mountGroup.add(vehicle);
    model.position.copy(seat);
  }

  get mounted(): boolean {
    return this.vehicle !== null;
  }

  /** Loop played by the rider while mounted and idle (a seated pose on creatures; null stands, as on the ship). */
  ridePose: string | null = null;

  // ------------------------------------------------------------------ parts

  /** Hides the meshes of a broken boss part (`BREAKABLE_MESHES`, or a manifest mesh named `<model>_<part>`). */
  setPartBroken(partId: string): void {
    if (this.brokenParts.has(partId) || !this.model) return;
    this.brokenParts.add(partId);
    const names = new Set<string>([...(BREAKABLE_MESHES[this.assetId]?.[partId] ?? []), `${this.assetId}_${partId}`]);
    this.model.traverse((o) => {
      if (names.has(o.name)) o.visible = false;
    });
    for (const hull of this.outline?.meshes ?? []) {
      if (!names.has(hull.name.replace(/_outline$/, ''))) continue;
      hull.visible = false;
      this.hiddenHulls.add(hull);
    }
  }

  isPartBroken(partId: string): boolean {
    return this.brokenParts.has(partId);
  }

  // ------------------------------------------------------------------ animation

  private resolveClip(name: string): string | null {
    if (this.clips.has(name)) return name;
    for (const alt of CLIP_FALLBACK[name] ?? []) {
      if (this.clips.has(alt)) return alt;
      for (const alt2 of CLIP_FALLBACK[alt] ?? []) if (this.clips.has(alt2)) return alt2;
    }
    return null;
  }

  private action(name: string): THREE.AnimationAction | null {
    if (!this.mixer) return null;
    const resolved = this.resolveClip(name);
    if (!resolved) return null;
    let a = this.actions.get(resolved);
    if (!a) {
      const clip = this.clips.get(resolved) as THREE.AnimationClip;
      a = this.mixer.clipAction(clip);
      this.actions.set(resolved, a);
    }
    return a;
  }

  /** Plays a clip; `duration` fits one-shot clips to a skill stage length. */
  play(name: string, fade = 0.12, duration?: number, loop?: boolean): void {
    const a = this.action(name);
    if (!a) return;
    const looping = loop ?? LOOPING.has(name);
    if (this.current === a && looping && a.isRunning()) return;
    a.reset();
    a.setLoop(looping ? THREE.LoopRepeat : THREE.LoopOnce, Infinity);
    a.clampWhenFinished = !looping;
    const clipDur = a.getClip().duration;
    a.timeScale = duration && !looping ? Math.min(2.2, Math.max(0.45, clipDur / duration)) : 1;
    a.enabled = true;
    a.setEffectiveWeight(1);
    if (this.current && this.current !== a) a.crossFadeFrom(this.current, fade, false);
    a.play();
    this.current = a;
    this.currentName = name;
    if (!looping) {
      this.oneShotName = name;
      this.oneShotUntil = this.time + (duration ?? clipDur / a.timeScale);
    } else {
      this.oneShotName = '';
    }
  }

  /** Starts the animation for a skill stage (called from castStage events). */
  playStage(castKey: string, anim: string, duration: number, looping: boolean): void {
    if (this.lastStageKey === castKey) return;
    this.lastStageKey = castKey;
    this.play(anim, 0.08, duration, looping);
  }

  endStage(): void {
    this.lastStageKey = '';
    this.oneShotUntil = 0;
  }

  hitFlash(): void {
    this.flashT = HIT_FLASH;
  }

  flinch(): void {
    if (this.oneShotUntil > this.time && this.oneShotName !== 'Hit') return;
    this.play('Hit', 0.05);
  }

  /**
   * Per-frame update. `animate` = false (off-screen) keeps the clip state
   * machine running but defers the skeleton update; `animStep` > 0 ticks the
   * mixer at that interval (far actors) instead of every frame.
   */
  update(dt: number, actor: Actor, alpha: number, heightAt: (x: number, z: number) => number, animate = true, animStep = 0): void {
    this.time += dt;
    // Interpolate between the last two sim positions.
    const x = lerp(actor.prevPos.x, actor.pos.x, alpha);
    const z = lerp(actor.prevPos.z, actor.pos.z, alpha);
    this.visualPos.set(x, heightAt(x, z), z);
    this.root.position.set(x, this.visualPos.y + actor.height, z);
    this.blobAlpha = actor.alive ? 0.9 : Math.max(0, 0.9 - (this.time - this.deathTime) * 0.3);
    this.yaw = lerpAngle(this.yaw, actor.facing, 1 - Math.exp(-18 * dt));
    this.root.rotation.y = this.yaw;

    this.updateState(actor);
    // Hands follow the clip (tools for Gather / Fish).
    if ((this.tool ?? this.autoTool()) !== this.appliedTool) this.syncHands();

    this.pendingAnim += dt;
    this.animClock += dt;
    if (this.mixer && animate && (animStep <= 0 || this.animClock >= animStep)) {
      this.mixer.update(Math.min(this.pendingAnim, 1));
      this.pendingAnim = 0;
      this.animClock = 0;
    }
    if (this.procedural) this.updateProcedural(dt, actor);
    if (this.mountGroup) {
      const sway = 1 + Math.min(1, actor.speed / 6);
      this.mountGroup.rotation.z = Math.sin(this.time * 0.8) * 0.03 * sway;
      this.mountGroup.rotation.x = Math.sin(this.time * 0.55 + 1.3) * 0.018 * sway;
      this.mountGroup.position.y = Math.sin(this.time * 1.1) * 0.07;
    }
    this.updateGlow(dt, actor);
  }

  private updateState(actor: Actor): void {
    if (!actor.alive) {
      if (!this.deathPlayed) {
        this.deathPlayed = true;
        this.deathTime = this.time;
        this.play('Death', 0.1);
      }
      return;
    }
    this.deathPlayed = false;
    const busy = this.oneShotUntil > this.time;
    if (actor.downTimer > 0 || actor.airTimer > 0) {
      if (this.currentName !== 'Knockdown') this.play('Knockdown', 0.08, 0.5, false);
    } else if (actor.statuses.some((s) => s.id === 'stun' || s.id === 'freeze' || s.id === 'sleep' || s.id === 'quake' || s.id === 'petrify' || s.id === 'imprison')) {
      this.play('Stun', 0.15, undefined, true);
    } else if (actor.statuses.some((s) => s.id === 'staggered') || (actor.monster?.countered ?? 0) > 0) {
      this.play('Stagger', 0.15, undefined, true);
    } else if (actor.cast) {
      // Cast animations are driven by castStage events; holding/charging loop.
      const c = actor.cast;
      if (c.phase === 'holding' || c.phase === 'charging' || c.phase === 'casting') {
        const anim = c.phase === 'holding' ? (c.skill.holding?.anim ?? 'Channel') : c.phase === 'charging' ? (c.skill.charge?.anim ?? 'Charge') : 'Channel';
        this.play(anim, 0.1, undefined, true);
      } else if (!busy && actor.speed > 0.4 && !this.vehicle) {
        this.play('Run', 0.15);
      }
    } else if (!busy || this.currentName === 'Knockdown' || this.currentName === 'Stun' || this.currentName === 'Stagger') {
      if (actor.speed > 0.4 && !this.vehicle) {
        this.play('Run', 0.15);
        const run = this.action('Run');
        if (run) run.timeScale = Math.min(1.6, Math.max(0.6, actor.speed / (4.8 * this.scale)));
      } else {
        this.play(this.vehicle && this.ridePose ? this.ridePose : 'Idle', 0.2, undefined, !!this.vehicle && !!this.ridePose);
      }
    }
  }

  /** Helper effects (`fx_*`): spin, bob, and a quick shrink when destroyed. */
  private updateProcedural(dt: number, actor: Actor): void {
    for (const s of this.spinners) s.rotation.y += dt * 1.6;
    for (const b of this.bobbers) b.position.y = 1.25 + Math.sin(this.time * 2.2) * 0.12;
    if (this.model) {
      const k = actor.alive ? 1 : Math.max(0.001, 1 - (this.time - this.deathTime) * 3);
      this.model.scale.setScalar(this.scale * k);
    }
  }

  private updateGlow(dt: number, actor: Actor): void {
    this.flashT = Math.max(0, this.flashT - dt);
    const flash = this.flashT > 0 ? (this.flashT / HIT_FLASH) * 1.2 : 0;
    const on = this.glow !== 'none' && actor.alive;
    if (on) {
      this.glowStyle = GLOWS[this.glow as Exclude<Glow, 'none'>];
      this.glowShown.copy(this.glow === 'identity' ? this.glowColor : this.glowStyle.color);
    }
    this.glowLevel = on ? Math.min(1, this.glowLevel + dt * 8) : Math.max(0, this.glowLevel - dt * 4);
    const s = this.glowStyle;
    this.glowPhase += dt * (this.glow === 'counter' && this.counterLeft > 0 && this.counterLeft < 0.6 ? s.hz * 2 : s.hz);
    const pulse = 1 - s.depth * (0.5 + 0.5 * Math.sin(this.glowPhase * Math.PI * 2));
    const g = this.glowLevel * pulse;
    this.uniforms.uGlowRim.value.copy(this.glowShown).multiplyScalar(s.rim * g);
    this.uniforms.uGlowPow.value = s.pow;
    this.uniforms.uGlowBody.value.copy(this.glowShown).multiplyScalar(s.body * g).addScalar(flash);

    const ou = this.outlineU;
    if (ou) {
      if (!actor.alive) {
        ou.uOutlinePx.value = Math.max(0, ou.uOutlinePx.value - dt * 6);
      } else if (on && s.outline) {
        ou.uOutlineColor.value.copy(s.outline).multiplyScalar(0.75 + 0.25 * pulse);
        ou.uOutlinePx.value = s.outlinePx;
      } else {
        ou.uOutlineColor.value.copy(BATTLE_OUTLINE);
        ou.uOutlinePx.value = 2.2;
      }
      const show = ou.uOutlinePx.value > 0.05;
      for (const m of this.outline?.meshes ?? []) m.visible = show && !this.hiddenHulls.has(m);
    }
  }

  /** Aura emission of the current glow: particles per second (0 = none) and colour. */
  get aura(): { rate: number; color: THREE.Color } {
    return { rate: this.glow !== 'none' ? this.glowStyle.aura * this.glowLevel : 0, color: this.glowShown };
  }

  /** Ground point under the actor (terrain height at its interpolated position). */
  get ground(): THREE.Vector3 {
    return this.visualPos;
  }

  /** World position of the head (for nameplates / floating text). */
  headWorld(out: THREE.Vector3): THREE.Vector3 {
    return out.set(this.root.position.x, this.root.position.y + this.headHeight, this.root.position.z);
  }

  /** Enables/disables real shadow casting for the body (blob shadows stay). */
  setCastShadow(on: boolean): void {
    this.model?.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh && !m.name.endsWith('_outline')) m.castShadow = on;
    });
  }

  dispose(): void {
    this.disposed = true;
    this.mixer?.stopAllAction();
    for (const m of this.materials) m.dispose();
    this.materials = [];
    this.outline?.material.dispose();
  }
}
